import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

interface FilterDropdownProps {
    title: string;
    options: string[];
    selectedOptions: string[];
    onChange: (selected: string[]) => void;
    align?: 'left' | 'center' | 'right';
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
    title,
    options,
    selectedOptions,
    onChange,
    align = 'center'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [activeIndex, setActiveIndex] = useState(-1);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const listboxId = useId();

    const filteredOptions = useMemo(
        () => options.filter((option) => option.toLowerCase().includes(search.toLowerCase())),
        [options, search]
    );

    const getAlignmentClasses = () => {
        switch (align) {
            case 'left':
                return 'left-0';
            case 'right':
                return 'right-0';
            case 'center':
            default:
                return 'left-1/2 -translate-x-1/2';
        }
    };

    const toggleDropdown = useCallback(() => {
        setIsOpen((prev) => {
            if (prev) {
                setSearch('');
                setActiveIndex(-1);
            }
            return !prev;
        });
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearch('');
                setActiveIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            searchInputRef.current?.focus();
        }
    }, [isOpen]);

    const handleOptionToggle = (option: string) => {
        const newSelectedOptions = selectedOptions.includes(option)
            ? selectedOptions.filter((item) => item !== option)
            : [...selectedOptions, option];
        onChange(newSelectedOptions);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(true);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex((prev) => Math.min(prev + 1, filteredOptions.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex((prev) => Math.max(prev - 1, 0));
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
                    handleOptionToggle(filteredOptions[activeIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setSearch('');
                setActiveIndex(-1);
                break;
        }
    };

    const getButtonText = () => title;

    return (
        <div
            className="relative inline-block text-left"
            ref={dropdownRef}
            onKeyDown={handleKeyDown}
        >
            <button
                type="button"
                className={`inline-flex justify-between items-center w-full rounded-full border-2 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none transition-colors duration-300 ${
                    isOpen ? 'border-gray-700' : 'border-gray-300'
                }`}
                onClick={toggleDropdown}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-controls={listboxId}
                style={{ minWidth: '160px' }}
            >
                <span>{getButtonText()}</span>
                <span className="flex items-center ml-2">
                    <span
                        className={`mr-1 flex items-center justify-center h-5 min-w-5 px-1 rounded-full text-xs leading-none transition-opacity duration-200 ${
                            selectedOptions.length > 0
                                ? 'bg-gray-700 text-white opacity-100'
                                : 'opacity-0'
                        }`}
                    >
                        {selectedOptions.length || 0}
                    </span>
                    <ChevronDownIcon
                        className={`h-5 w-5 transition-transform duration-300 ${
                            isOpen ? 'transform rotate-180' : ''
                        }`}
                    />
                </span>
            </button>

            <div
                id={listboxId}
                role="listbox"
                aria-label={`${title} filter options`}
                aria-multiselectable="true"
                className={`absolute ${getAlignmentClasses()} mt-2 w-64 z-20 bg-white shadow-lg rounded-2xl transition-all duration-300 ease-in-out overflow-hidden ring-2 ${
                    isOpen ? 'ring-gray-700' : 'ring-transparent'
                }`}
                style={{
                    maxHeight: isOpen ? '24rem' : '0',
                    opacity: isOpen ? 1 : 0
                }}
            >
                <div className="sticky top-0 bg-white px-3 pt-3 pb-2">
                    <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-700"
                            placeholder={`Search ${title.toLowerCase()}...`}
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setActiveIndex(-1);
                            }}
                            aria-label={`Search ${title.toLowerCase()} options`}
                        />
                    </div>
                    {selectedOptions.length > 0 && (
                        <button
                            type="button"
                            className="flex items-center gap-1 mt-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                            onClick={() => onChange([])}
                        >
                            <XMarkIcon className="h-3.5 w-3.5" />
                            Clear {selectedOptions.length} selected
                        </button>
                    )}
                </div>
                <div className="overflow-y-auto scrollbar-hide pb-1" style={{ maxHeight: '16rem' }}>
                    {filteredOptions.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-400 text-center">
                            No matches
                        </div>
                    ) : (
                        filteredOptions.map((option, index) => (
                            <div
                                key={option}
                                role="option"
                                aria-selected={selectedOptions.includes(option)}
                                className={`flex items-center px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer ${
                                    index === activeIndex ? 'bg-gray-100' : ''
                                }`}
                                onClick={() => handleOptionToggle(option)}
                            >
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 text-gray-700 border-gray-300 rounded focus:ring-gray-500 pointer-events-none"
                                    checked={selectedOptions.includes(option)}
                                    readOnly
                                    tabIndex={-1}
                                    style={{
                                        accentColor: selectedOptions.includes(option)
                                            ? '#0A0A0A'
                                            : '#D4D4D4'
                                    }}
                                />
                                <label className="ml-3 min-w-0 flex-1 text-gray-700 select-none cursor-pointer">
                                    {option}
                                </label>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default FilterDropdown;
